/**
 * Collapsible JSON Viewer Component
 * Renders JSON with expandable/collapsible nodes
 */

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';

interface JsonViewerProps {
  data: unknown;
  defaultExpanded?: boolean;
  className?: string;
  colorScheme?: 'green' | 'blue' | 'red';
}

interface JsonNodeProps {
  keyName?: string;
  value: unknown;
  depth: number;
  isLast: boolean;
  expandedPaths: Set<string>;
  path: string;
  onToggle: (path: string) => void;
  colorScheme: 'green' | 'blue' | 'red';
}

const colorSchemes = {
  green: {
    key: 'text-purple-400',
    string: 'text-green-400',
    number: 'text-yellow-400',
    boolean: 'text-orange-400',
    null: 'text-gray-500',
    bracket: 'text-gray-300',
  },
  blue: {
    key: 'text-purple-400',
    string: 'text-blue-400',
    number: 'text-yellow-400',
    boolean: 'text-orange-400',
    null: 'text-gray-500',
    bracket: 'text-gray-300',
  },
  red: {
    key: 'text-purple-400',
    string: 'text-red-300',
    number: 'text-yellow-400',
    boolean: 'text-orange-400',
    null: 'text-gray-500',
    bracket: 'text-gray-300',
  },
};

function JsonNode({ keyName, value, depth, isLast, expandedPaths, path, onToggle, colorScheme }: JsonNodeProps) {
  const colors = colorSchemes[colorScheme];
  const indent = depth * 16;
  const isExpanded = expandedPaths.has(path);

  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);

  if (!isObject) {
    // Primitive value
    let displayValue: React.ReactNode;
    let colorClass = colors.string;

    if (typeof value === 'string') {
      displayValue = `"${value}"`;
      colorClass = colors.string;
    } else if (typeof value === 'number') {
      displayValue = String(value);
      colorClass = colors.number;
    } else if (typeof value === 'boolean') {
      displayValue = String(value);
      colorClass = colors.boolean;
    } else if (value === null) {
      displayValue = 'null';
      colorClass = colors.null;
    } else {
      displayValue = String(value);
    }

    return (
      <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
        {keyName !== undefined && (
          <>
            <span className={colors.key}>"{keyName}"</span>
            <span className={colors.bracket}>: </span>
          </>
        )}
        <span className={colorClass}>{displayValue}</span>
        {!isLast && <span className={colors.bracket}>,</span>}
      </div>
    );
  }

  // Object or Array
  const entries = isArray ? value.map((v, i) => [i, v] as [number, unknown]) : Object.entries(value as Record<string, unknown>);
  const isEmpty = entries.length === 0;
  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';

  if (isEmpty) {
    return (
      <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
        {keyName !== undefined && (
          <>
            <span className={colors.key}>"{keyName}"</span>
            <span className={colors.bracket}>: </span>
          </>
        )}
        <span className={colors.bracket}>{openBracket}{closeBracket}</span>
        {!isLast && <span className={colors.bracket}>,</span>}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{ paddingLeft: indent }}
        className="flex items-center py-0.5 cursor-pointer hover:bg-white/5 rounded"
        onClick={() => onToggle(path)}
      >
        <span className="w-4 h-4 flex items-center justify-center mr-1 text-gray-500">
          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </span>
        {keyName !== undefined && (
          <>
            <span className={colors.key}>"{keyName}"</span>
            <span className={colors.bracket}>: </span>
          </>
        )}
        <span className={colors.bracket}>{openBracket}</span>
        {!isExpanded && (
          <>
            <span className="text-gray-500 text-xs mx-1">
              {isArray ? `${entries.length} items` : `${entries.length} keys`}
            </span>
            <span className={colors.bracket}>{closeBracket}</span>
            {!isLast && <span className={colors.bracket}>,</span>}
          </>
        )}
      </div>
      {isExpanded && (
        <>
          {entries.map(([key, val], index) => (
            <JsonNode
              key={String(key)}
              keyName={isArray ? undefined : String(key)}
              value={val}
              depth={depth + 1}
              isLast={index === entries.length - 1}
              expandedPaths={expandedPaths}
              path={`${path}.${key}`}
              onToggle={onToggle}
              colorScheme={colorScheme}
            />
          ))}
          <div style={{ paddingLeft: indent }} className="py-0.5">
            <span className="w-4 inline-block" />
            <span className={colors.bracket}>{closeBracket}</span>
            {!isLast && <span className={colors.bracket}>,</span>}
          </div>
        </>
      )}
    </div>
  );
}

export function JsonViewer({ data, defaultExpanded = false, className, colorScheme = 'green' }: JsonViewerProps) {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => {
    if (defaultExpanded) {
      // Expand all paths
      const paths = new Set<string>();
      const collectPaths = (obj: unknown, path: string) => {
        if (obj !== null && typeof obj === 'object') {
          paths.add(path);
          if (Array.isArray(obj)) {
            obj.forEach((item, i) => collectPaths(item, `${path}.${i}`));
          } else {
            Object.entries(obj as Record<string, unknown>).forEach(([key, val]) => {
              collectPaths(val, `${path}.${key}`);
            });
          }
        }
      };
      collectPaths(data, 'root');
      return paths;
    }
    return new Set<string>();
  });

  const handleToggle = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const paths = new Set<string>();
    const collectPaths = (obj: unknown, path: string) => {
      if (obj !== null && typeof obj === 'object') {
        paths.add(path);
        if (Array.isArray(obj)) {
          obj.forEach((item, i) => collectPaths(item, `${path}.${i}`));
        } else {
          Object.entries(obj as Record<string, unknown>).forEach(([key, val]) => {
            collectPaths(val, `${path}.${key}`);
          });
        }
      }
    };
    collectPaths(data, 'root');
    setExpandedPaths(paths);
  }, [data]);

  const collapseAll = useCallback(() => {
    setExpandedPaths(new Set());
  }, []);

  return (
    <div className={cn('font-mono text-xs', className)}>
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-700">
        <button
          onClick={expandAll}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
        >
          <ChevronDown className="h-3 w-3" />
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
        >
          <Minus className="h-3 w-3" />
          Collapse All
        </button>
      </div>
      <JsonNode
        value={data}
        depth={0}
        isLast={true}
        expandedPaths={expandedPaths}
        path="root"
        onToggle={handleToggle}
        colorScheme={colorScheme}
      />
    </div>
  );
}

export default JsonViewer;
